import React from 'react';
import Label from '~/components/template/EventTimelineV2/components/Block/components/Data/components/Label';
import Link from '~/components/molecule/Link';
import Markdown from '~/components/molecule/Markdown';
import {
  Body,
  Heading4,
  Heading5,
  Heading6,
  Strong,
  Variant,
} from '~/components/atom/Typography';
import { MarkdownBlock } from '~/graphql/types';
import styled, { css } from 'styled-components';
import { BLUR_STYLES } from '../styles';
import type { SystemSize } from '~/theme';
import RelevanceScoreWrapper from '../../RelevanceScoreWrapper';

export type Props = Omit<MarkdownBlock, '__typename' | 'id'> & {
  dataTestId?: string;
  margin?: Array<SystemSize | null>;
};

const MarkdownBlockComponent: React.FC<Props> = ({
  css,
  label,
  value,
  margin,
  blurred,
  relevanceScore,
}) => (
  <RelevanceScoreWrapper relevanceScore={relevanceScore}>
    {label && <Label>{label}</Label>}

    <StyledMarkdown
      value={value}
      options={{
        remarkReactComponents: getComponents({ css, margin }),
      }}
      $blurred={blurred || false}
    />
  </RelevanceScoreWrapper>
);

export default MarkdownBlockComponent;

const StyledMarkdown = styled(Markdown)<{ $blurred: boolean }>(
  ({ $blurred }) => css`
    ${$blurred && BLUR_STYLES};
  `,
);

const getComponents = (extraProps?: {
  css?: string | null;
  margin?: Array<SystemSize | null>;
}) => ({
  h1: props => (
    <Heading4
      {...props}
      {...extraProps}
      variant={Variant.primary}
      color={{ group: 'primary' }}
      css={extraProps?.css}
      fontWeight="semiBold"
      size="m"
    />
  ),
  h2: props => (
    <Heading5
      {...props}
      {...extraProps}
      fontWeight="medium"
      color={{ group: 'text' }}
      css={extraProps?.css}
      size="base"
    />
  ),
  h3: props => (
    <Heading6
      {...props}
      {...extraProps}
      size="base"
      color={{ group: 'tertiary', variant: 'dark' }}
      css={extraProps?.css}
    />
  ),
  strong: props => (
    <Strong
      {...props}
      {...extraProps}
      color={{ group: 'secondary' }}
      css={extraProps?.css}
      style={{ fontSize: 'inherit' }}
    />
  ),
  p: props => (
    <Body size="base" {...props} {...extraProps} css={extraProps?.css} />
  ),
  a: props => (
    <StyledLink
      {...props}
      {...extraProps}
      to={props.href}
      css={extraProps?.css}
    />
  ),
});

const StyledLink = styled(Link)(
  ({ theme }) => css`
    color: ${theme.color('secondary')};
  `,
);
